import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { FocusOrigin } from '@angular/cdk/a11y';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store/state';
import { UserManagementDialogComponent } from '../user-management-dialog/user-management-dialog.component';
import { SharedService } from '../../services/shared.service';
import { Profile } from '../../models/auth/profile';
import { Role } from '../../models/auth/role';
import { Menu, orgType } from '../../../config/app-constants';
import { AppComponent } from 'src/app/app.component';
import { PermissionGroupsDialogComponent } from '../permission-groups-dialog/permission-groups-dialog.component';
import { SystemAlertsDialogComponent } from 'src/app/internal-user/notifications/components/system-alerts-dialog/system-alerts-dialog.component';
import { catchError, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SiteMoveService } from 'src/app/internal-user/customer-management/services/site-move.service';
import { SiteMoveHistoryDialogComponent } from '../site-move-history-dialog/site-move-history-dialog.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() menuClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() cdkFocusChange: EventEmitter<FocusOrigin> = new EventEmitter<FocusOrigin>();
  @Input() enablePharmacyPeek: boolean;

  profile: Profile;
  message: string;
  enableGearIcon = false;
  enableGearIconUserManagement = false;
  enableGearIconPermissionGroup = false;
  enableGearIconSiteMoveHistory = false;
  enableNotificationsIcon = false;

  Role = Role;
  private readonly destroyed = new Subject<void>();
  private historyDialog: MatDialogRef<SiteMoveHistoryDialogComponent> | undefined;

  constructor(
    public auth: AuthService,
    public dialog: MatDialog,
    private readonly messageService: SharedService,
    public myapp: AppComponent,
    public store: Store<State>,
    private readonly siteMoves: SiteMoveService,
  ) { }

  ngOnInit() {
    this.messageService.sharedMessage.pipe(takeUntil(this.destroyed))
      .subscribe(message => this.message = message);
    this.auth.profile$.pipe(
      switchMap(profile => {
        this.profile = profile;
        this.enableGearIconSiteMoveHistory = false;
        this.enableGearIconUserManagement = profile.menuPermissions.includes(Menu.GearIconUserManagement);
        this.enableGearIconPermissionGroup = profile.menuPermissions.includes(Menu.GearIconPermissionGroup);
        this.enableGearIcon = this.enableGearIconUserManagement || this.enableGearIconPermissionGroup;
        this.enableNotificationsIcon = profile.hasSystemAlertingAccess();
        // Older internal profiles may omit orgType; external users never request this data.
        if (this.auth.loggedIn && profile.orgType !== orgType.EXTERNAL
          && profile.hasAnyRoleOf(Role.admin, Role.parataDev)) {
          return this.siteMoves.access('read').pipe(catchError(() => of({ allowed: false })));
        }
        return of({ allowed: false });
      }),
      takeUntil(this.destroyed),
    ).subscribe(access => {
      this.enableGearIconSiteMoveHistory = access?.allowed === true;
      this.enableGearIcon = this.enableGearIconUserManagement || this.enableGearIconPermissionGroup
        || this.enableGearIconSiteMoveHistory;
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.enableGearIconSiteMoveHistory = false;
    this.historyDialog?.close();
  }

  openSiteMoveHistory(): void {
    if (!this.auth.loggedIn || !this.enableGearIconSiteMoveHistory || this.historyDialog) { return; }
    this.historyDialog = this.dialog.open(SiteMoveHistoryDialogComponent, {
      width: 'min(1120px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(800px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 32px)',
      autoFocus: 'dialog',
      ariaLabelledBy: 'site-move-history-title',
    });
    this.historyDialog.afterClosed().pipe(takeUntil(this.destroyed))
      .subscribe(() => { this.historyDialog = undefined; });
  }

  openUserManagement() {
    if (this.auth.loggedIn && this.enableGearIconUserManagement) {
      this.dialog.open(
        UserManagementDialogComponent,
        { disableClose: true, backdropClass: ['usermgmtDialog','lgDialog', 'SnackDialog'] },
      );
    }
  }

  openPermissionGroups() {
    if (this.auth.loggedIn && this.enableGearIconPermissionGroup) {
      this.dialog.open(
        PermissionGroupsDialogComponent,
        { disableClose: true, backdropClass: ['PermissionGrpsDialog','lgDialog', 'SnackDialog'] },
      );
    }
  }


  openPharmacyPeekDialog() {
    this.myapp.openPharmacyPeekDialog();
    return;
  }

  onClickNotifications() 
  {
    if (this.auth.loggedIn && this.enableNotificationsIcon) {
      this.dialog.open(
        SystemAlertsDialogComponent,
        { disableClose: false, backdropClass: ['notificationsDialog','lgDialog', 'SnackDialog'] },
      );
    }
  }
}
